import React, { useState, useEffect, useRef } from 'react';
import { Image as ImageIcon, Upload, Plus, Trash2, GripVertical, Save, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { adminAPI } from '../../api';
import { toast } from 'sonner';
import { formatApiError } from '../../lib/apiError';

export default function SiteEditor() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [openSection, setOpenSection] = useState('hero');

  useEffect(() => {
    adminAPI.getSiteSettings().then(res => {
      const d = res.data;
      delete d._id;
      delete d._type;
      delete d.updated_at;
      setSettings(d);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load settings'); setLoading(false); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSiteSettings(settings);
      toast.success('Settings saved!');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const update = (path, value) => {
    setSettings(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) {
        const k = isNaN(keys[i]) ? keys[i] : parseInt(keys[i]);
        obj = obj[k];
      }
      const lastKey = isNaN(keys[keys.length - 1]) ? keys[keys.length - 1] : parseInt(keys[keys.length - 1]);
      obj[lastKey] = value;
      return copy;
    });
  };

  if (loading) return <div className="text-center py-16 text-sm text-[#6f6a63]">Loading settings...</div>;
  if (!settings) return <div className="text-center py-16 text-sm text-red-500">Failed to load settings</div>;

  const sections = [
    { key: 'hero', label: 'Hero Banner / Slider' },
    { key: 'weekly_specials', label: 'Weekly Specials' },
    { key: 'prebook_banner', label: 'Prebook Banner' },
    { key: 'shop_by_category', label: 'Shop by Category' },
    { key: 'about_us', label: 'About Us' },
    { key: 'instagram', label: 'Find Us on Instagram' },
    { key: 'newsletter', label: 'Newsletter' },
    { key: 'footer', label: 'Footer' },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-lg font-semibold tracking-wider uppercase">Site Editor</h1>
        <button data-testid="site-editor-save" onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#2f3e36] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#253029] transition-colors disabled:opacity-50">
          <Save size={16} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>

      <div className="space-y-2">
        {sections.map(sec => (
          <div key={sec.key} className="bg-white border border-[#e7e3dd] rounded-xl overflow-hidden">
            <button
              onClick={() => setOpenSection(openSection === sec.key ? '' : sec.key)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-[#fbfaf8] transition-colors"
            >
              <span className="text-sm font-semibold tracking-wider uppercase">{sec.label}</span>
              {openSection === sec.key ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {openSection === sec.key && (
              <div className="px-5 pb-5 border-t border-[#e7e3dd]">
                {sec.key === 'hero' && <HeroEditor data={settings.hero} update={(p, v) => update(`hero.${p}`, v)} />}
                {sec.key === 'weekly_specials' && <WeeklySpecialsEditor data={settings.weekly_specials} update={(p, v) => update(`weekly_specials.${p}`, v)} />}
                {sec.key === 'prebook_banner' && <PrebookEditor data={settings.prebook_banner} update={(p, v) => update(`prebook_banner.${p}`, v)} />}
                {sec.key === 'shop_by_category' && <CategoryEditor data={settings.shop_by_category} update={(p, v) => update(`shop_by_category.${p}`, v)} setSettings={setSettings} />}
                {sec.key === 'about_us' && <AboutEditor data={settings.about_us} update={(p, v) => update(`about_us.${p}`, v)} />}
                {sec.key === 'instagram' && <InstagramEditor data={settings.instagram} update={(p, v) => update(`instagram.${p}`, v)} />}
                {sec.key === 'newsletter' && <NewsletterEditor data={settings.newsletter} update={(p, v) => update(`newsletter.${p}`, v)} />}
                {sec.key === 'footer' && <FooterEditor data={settings.footer} update={(p, v) => update(`footer.${p}`, v)} setSettings={setSettings} />}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving} className="flex items-center gap-2 bg-[#2f3e36] text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-[#253029] transition-colors disabled:opacity-50">
          <Save size={16} /> {saving ? 'Saving...' : 'Save All Changes'}
        </button>
      </div>
    </div>
  );
}

// Reusable image upload field
function ImageField({ label, value, onChange }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadImage(file);
      onChange(res.data.url);
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div>
      <label className="text-xs text-[#6f6a63] block mb-1">{label}</label>
      <div className="flex items-start gap-3">
        {value && (
          <img src={value} alt="" className="w-20 h-20 rounded-lg border border-[#e7e3dd] object-cover flex-shrink-0" />
        )}
        <div className="flex-1">
          <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="Image URL" className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-xs mb-2" />
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading} className="flex items-center gap-1 text-[10px] text-[#2f6b4f] font-medium hover:underline disabled:opacity-50">
            <Upload size={12} /> {uploading ? 'Uploading...' : 'Upload image'}
          </button>
          <input ref={ref} type="file" accept="image/*" className="hidden" onChange={e => handleFile(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
}

function TextField({ label, value, onChange, multiline, placeholder }) {
  return (
    <div>
      <label className="text-xs text-[#6f6a63] block mb-1">{label}</label>
      {multiline ? (
        <textarea rows={4} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-xs" />
      ) : (
        <input type="text" value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-xs" />
      )}
    </div>
  );
}

// ========== SECTION EDITORS ==========

function HeroEditor({ data, update }) {
  const slides = data?.slides || [];

  const addSlide = () => {
    const newSlides = [...slides, { image_url: '', subtitle: '', title: '', button1_text: 'Shop Now', button1_link: '/shop', button2_text: '', button2_link: '' }];
    update('slides', newSlides);
  };

  const removeSlide = (i) => {
    update('slides', slides.filter((_, idx) => idx !== i));
  };

  const updateSlide = (i, field, value) => {
    const newSlides = [...slides];
    newSlides[i] = { ...newSlides[i], [field]: value };
    update('slides', newSlides);
  };

  return (
    <div className="pt-4 space-y-4">
      <p className="text-xs text-[#6f6a63]">Configure the hero banner slides. The first slide is shown by default.</p>
      {slides.map((slide, i) => (
        <div key={i} className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Slide {i + 1}</span>
            {slides.length > 1 && <button onClick={() => removeSlide(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>}
          </div>
          <ImageField label="Background Image" value={slide.image_url} onChange={v => updateSlide(i, 'image_url', v)} />
          <TextField label="Subtitle (small text above title)" value={slide.subtitle} onChange={v => updateSlide(i, 'subtitle', v)} />
          <TextField label="Title (main heading)" value={slide.title} onChange={v => updateSlide(i, 'title', v)} />
          <div className="grid grid-cols-2 gap-3">
            <TextField label="Button 1 Text" value={slide.button1_text} onChange={v => updateSlide(i, 'button1_text', v)} />
            <TextField label="Button 1 Link" value={slide.button1_link} onChange={v => updateSlide(i, 'button1_link', v)} />
            <TextField label="Button 2 Text" value={slide.button2_text} onChange={v => updateSlide(i, 'button2_text', v)} />
            <TextField label="Button 2 Link" value={slide.button2_link} onChange={v => updateSlide(i, 'button2_link', v)} />
          </div>
        </div>
      ))}
      <button onClick={addSlide} className="flex items-center gap-1 text-xs text-[#2f6b4f] font-medium hover:underline">
        <Plus size={14} /> Add Slide
      </button>
    </div>
  );
}

function WeeklySpecialsEditor({ data, update }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [catalogCount, setCatalogCount] = useState(0);
  const excelRef = useRef(null);

  useEffect(() => {
    adminAPI.getProducts({ limit: 1000 }).then(res => {
      const loaded = res.data.products || [];
      setProducts(loaded);
      // A limited product page cannot establish that a saved ID is orphaned.
      // Never remove CMS references merely by opening the editor.
    }).catch(() => {});
    import('../../api').then(({ specialsAPI }) => {
      specialsAPI.getSpecialsCatalog({ limit: 1 }).then(res => setCatalogCount(res.data.total)).catch(() => {});
    });
  }, []);

  const selectedIds = data?.product_ids || [];
  const MAX_FEATURED = 4;

  const toggleProduct = (id) => {
    if (selectedIds.includes(id)) {
      update('product_ids', selectedIds.filter(x => x !== id));
    } else {
      if (selectedIds.length >= MAX_FEATURED) {
        toast.error(`Maximum ${MAX_FEATURED} products allowed`);
        return;
      }
      update('product_ids', [...selectedIds, id]);
    }
  };

  const handleExcelUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminAPI.uploadSpecialsExcel(file);
      toast.success(res.data.message);
      setCatalogCount(res.data.imported);
    } catch (err) {
      toast.error(formatApiError(err, 'Upload failed'));
    } finally {
      setUploading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    (p.category || '').toLowerCase().includes(search.toLowerCase())
  );

  const selectedProducts = selectedIds.map(id => products.find(p => p._id === id)).filter(Boolean);

  return (
    <div className="pt-4 space-y-4">
      {/* Specials Catalog Excel Upload */}
      <div className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-lg p-4">
        <h4 className="text-xs font-semibold uppercase tracking-wider mb-2">Specials Catalog (Excel Upload)</h4>
        <p className="text-[10px] text-[#6f6a63] mb-3">Upload an Excel file (.xlsx) to populate the Weekly Specials page. Columns: Name, Category, Color, Image URL, Description.</p>
        <div className="flex items-center gap-3">
          <button
            onClick={() => excelRef.current?.click()}
            disabled={uploading}
            className="flex items-center gap-1.5 bg-[#2f3e36] text-white px-4 py-2 rounded-lg text-xs font-medium hover:bg-[#253029] transition-colors disabled:opacity-50"
          >
            <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Excel'}
          </button>
          <input ref={excelRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={e => handleExcelUpload(e.target.files[0])} />
          <span className="text-xs text-[#6f6a63]">{catalogCount} products in catalog</span>
        </div>
      </div>

      <hr className="border-[#e7e3dd]" />

      {/* Homepage Section Settings */}
      <h4 className="text-xs font-semibold uppercase tracking-wider">Homepage Section</h4>
      <TextField label="Section Title" value={data?.title} onChange={v => update('title', v)} />
      <TextField label="Link Text" value={data?.link_text} onChange={v => update('link_text', v)} />
      <TextField label="Link URL" value={data?.link_url} onChange={v => update('link_url', v)} />

      {/* Selected products preview */}
      {selectedProducts.length > 0 && (
        <div>
          <label className="text-xs text-[#6f6a63] block mb-2">Selected Products ({selectedProducts.length}/{MAX_FEATURED})</label>
          <div className="grid grid-cols-4 gap-2 mb-3">
            {selectedProducts.map((p, i) => (
              <div key={p._id} className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-lg p-2 text-center relative">
                <img src={p.images?.[0] || ''} alt={p.name} className="w-full aspect-square object-cover rounded mb-1" />
                <p className="text-[10px] font-medium truncate">{p.name}</p>
                <button onClick={() => toggleProduct(p._id)} className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[9px] hover:bg-red-600">x</button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="text-xs text-[#6f6a63] block mb-2">
          Choose up to {MAX_FEATURED} products {selectedIds.length > 0 ? `(${selectedIds.length} selected)` : '(leave empty to show all weekly specials)'}
        </label>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products..." className="w-full px-3 py-2 border border-[#e7e3dd] rounded-lg text-xs mb-2" />
        <div className="max-h-60 overflow-y-auto border border-[#e7e3dd] rounded-lg">
          {filteredProducts.length === 0 ? (
            <p className="text-xs text-[#6f6a63] p-3">No products found.</p>
          ) : filteredProducts.map(p => (
            <label key={p._id} className={`flex items-center gap-2 px-3 py-2 hover:bg-[#fbfaf8] cursor-pointer border-b border-[#f0efed] last:border-0 ${selectedIds.includes(p._id) ? 'bg-[#e7f1ea]' : ''}`}>
              <input
                type="checkbox"
                checked={selectedIds.includes(p._id)}
                onChange={() => toggleProduct(p._id)}
                disabled={!selectedIds.includes(p._id) && selectedIds.length >= MAX_FEATURED}
                className="w-3.5 h-3.5"
              />
              <img src={p.images?.[0] || ''} alt="" className="w-6 h-6 rounded object-cover border border-[#e7e3dd]" />
              <span className="text-xs flex-1">{p.name}</span>
              <span className="text-[10px] text-[#6f6a63]">{p.category}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function PrebookEditor({ data, update }) {
  return (
    <div className="pt-4 space-y-3">
      <TextField label="Title" value={data?.title} onChange={v => update('title', v)} />
      <TextField label="Subtitle" value={data?.subtitle} onChange={v => update('subtitle', v)} multiline />
      <TextField label="Button Text" value={data?.button_text} onChange={v => update('button_text', v)} />
      <TextField label="Button Link" value={data?.button_link} onChange={v => update('button_link', v)} />
      <ImageField label="Background Image" value={data?.image_url} onChange={v => update('image_url', v)} />
    </div>
  );
}

function CategoryEditor({ data, update, setSettings }) {
  const cats = data?.categories || [];

  const addCategory = () => {
    setSettings(prev => ({
      ...prev,
      shop_by_category: {
        ...prev.shop_by_category,
        categories: [...(prev.shop_by_category?.categories || []), { name: '', link: '/shop?category=', image_url: '' }]
      }
    }));
  };

  const removeCategory = (i) => {
    setSettings(prev => ({
      ...prev,
      shop_by_category: {
        ...prev.shop_by_category,
        categories: prev.shop_by_category.categories.filter((_, idx) => idx !== i)
      }
    }));
  };

  const updateCat = (i, field, value) => {
    setSettings(prev => {
      const newCats = [...prev.shop_by_category.categories];
      newCats[i] = { ...newCats[i], [field]: value };
      return { ...prev, shop_by_category: { ...prev.shop_by_category, categories: newCats } };
    });
  };

  return (
    <div className="pt-4 space-y-3">
      <TextField label="Section Title" value={data?.title} onChange={v => update('title', v)} />
      <label className="text-xs text-[#6f6a63] block">Categories (displayed as 2x2 grid on homepage)</label>
      {cats.map((cat, i) => (
        <div key={i} className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold">Category {i + 1}</span>
            <button onClick={() => removeCategory(i)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
          </div>
          <TextField label="Name" value={cat.name} onChange={v => updateCat(i, 'name', v)} />
          <TextField label="Link" value={cat.link} onChange={v => updateCat(i, 'link', v)} />
          <ImageField label="Image" value={cat.image_url} onChange={v => updateCat(i, 'image_url', v)} />
        </div>
      ))}
      <button onClick={addCategory} className="flex items-center gap-1 text-xs text-[#2f6b4f] font-medium hover:underline">
        <Plus size={14} /> Add Category
      </button>
    </div>
  );
}

function AboutEditor({ data, update }) {
  return (
    <div className="pt-4 space-y-3">
      <TextField label="Title" value={data?.title} onChange={v => update('title', v)} />
      <TextField label="Subtitle" value={data?.subtitle} onChange={v => update('subtitle', v)} />
      <TextField label="Text" value={data?.text} onChange={v => update('text', v)} multiline />
      <ImageField label="Image" value={data?.image_url} onChange={v => update('image_url', v)} />
    </div>
  );
}

function InstagramEditor({ data, update }) {
  return (
    <div className="pt-4 space-y-3">
      <TextField label="Title (use \\n for line break)" value={data?.title} onChange={v => update('title', v)} multiline />
      <TextField label="Text" value={data?.text} onChange={v => update('text', v)} multiline />
      <ImageField label="Image" value={data?.image_url} onChange={v => update('image_url', v)} />
    </div>
  );
}

function NewsletterEditor({ data, update }) {
  return (
    <div className="pt-4 space-y-3">
      <TextField label="Title" value={data?.title} onChange={v => update('title', v)} />
      <TextField label="Subtitle" value={data?.subtitle} onChange={v => update('subtitle', v)} />
      <ImageField label="Background Image (tulips area)" value={data?.background_image} onChange={v => update('background_image', v)} />
    </div>
  );
}

function FooterEditor({ data, update, setSettings }) {
  const menus = data?.menus || [];
  const social = data?.social_links || {};

  const addMenu = () => {
    setSettings(prev => ({
      ...prev,
      footer: { ...prev.footer, menus: [...(prev.footer?.menus || []), { title: 'NEW MENU', links: [{ text: '', url: '' }] }] }
    }));
  };

  const removeMenu = (i) => {
    setSettings(prev => ({
      ...prev,
      footer: { ...prev.footer, menus: prev.footer.menus.filter((_, idx) => idx !== i) }
    }));
  };

  const updateMenu = (i, field, value) => {
    setSettings(prev => {
      const newMenus = [...prev.footer.menus];
      newMenus[i] = { ...newMenus[i], [field]: value };
      return { ...prev, footer: { ...prev.footer, menus: newMenus } };
    });
  };

  const addLink = (menuIdx) => {
    setSettings(prev => {
      const newMenus = [...prev.footer.menus];
      newMenus[menuIdx] = { ...newMenus[menuIdx], links: [...newMenus[menuIdx].links, { text: '', url: '' }] };
      return { ...prev, footer: { ...prev.footer, menus: newMenus } };
    });
  };

  const removeLink = (menuIdx, linkIdx) => {
    setSettings(prev => {
      const newMenus = [...prev.footer.menus];
      newMenus[menuIdx] = { ...newMenus[menuIdx], links: newMenus[menuIdx].links.filter((_, i) => i !== linkIdx) };
      return { ...prev, footer: { ...prev.footer, menus: newMenus } };
    });
  };

  const updateLink = (menuIdx, linkIdx, field, value) => {
    setSettings(prev => {
      const newMenus = [...prev.footer.menus];
      const newLinks = [...newMenus[menuIdx].links];
      newLinks[linkIdx] = { ...newLinks[linkIdx], [field]: value };
      newMenus[menuIdx] = { ...newMenus[menuIdx], links: newLinks };
      return { ...prev, footer: { ...prev.footer, menus: newMenus } };
    });
  };

  return (
    <div className="pt-4 space-y-4">
      <TextField label="Copyright Text" value={data?.copyright_text} onChange={v => update('copyright_text', v)} />

      <div>
        <label className="text-xs text-[#6f6a63] block mb-2">Social Links</label>
        <div className="grid grid-cols-2 gap-3">
          <TextField label="Instagram URL" value={social.instagram} onChange={v => update('social_links', { ...social, instagram: v })} />
          <TextField label="Facebook URL" value={social.facebook} onChange={v => update('social_links', { ...social, facebook: v })} />
          <TextField label="Pinterest URL" value={social.pinterest || ''} onChange={v => update('social_links', { ...social, pinterest: v })} />
          <TextField label="Twitter / X URL" value={social.twitter || ''} onChange={v => update('social_links', { ...social, twitter: v })} />
        </div>
      </div>

      <div>
        <label className="text-xs text-[#6f6a63] block mb-2">Footer Menus</label>
        {menus.map((menu, mi) => (
          <div key={mi} className="bg-[#fbfaf8] border border-[#e7e3dd] rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <input type="text" value={menu.title} onChange={e => updateMenu(mi, 'title', e.target.value)} className="text-xs font-semibold uppercase bg-transparent border-b border-transparent hover:border-[#e7e3dd] focus:border-[#2f6b4f] focus:outline-none px-0 py-1" />
              <button onClick={() => removeMenu(mi)} className="text-red-500 hover:text-red-700"><Trash2 size={13} /></button>
            </div>
            {menu.links.map((link, li) => (
              <div key={li} className="flex items-center gap-2 mb-1.5">
                <input type="text" value={link.text} onChange={e => updateLink(mi, li, 'text', e.target.value)} placeholder="Text" className="flex-1 px-2 py-1 border border-[#e7e3dd] rounded text-xs" />
                <input type="text" value={link.url} onChange={e => updateLink(mi, li, 'url', e.target.value)} placeholder="URL" className="flex-1 px-2 py-1 border border-[#e7e3dd] rounded text-xs" />
                <button onClick={() => removeLink(mi, li)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
              </div>
            ))}
            <button onClick={() => addLink(mi)} className="flex items-center gap-1 text-[10px] text-[#2f6b4f] font-medium hover:underline mt-1">
              <Plus size={12} /> Add Link
            </button>
          </div>
        ))}
        <button onClick={addMenu} className="flex items-center gap-1 text-xs text-[#2f6b4f] font-medium hover:underline">
          <Plus size={14} /> Add Menu Section
        </button>
      </div>
    </div>
  );
}
